import { useUploadFile } from '@/api/file/file.hooks';
import { useGetThemes, useUpdateCategory } from '@/api/glow-up/glow-up.hooks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Category, UpdateCategoryRequest } from '@repo/types/src/glow-up';
import { Edit, Upload, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

type EditCategoryProps = {
  category: Category;
  refetch: () => void;
};

export const EditCategory = ({ category, refetch }: EditCategoryProps) => {
  const mutation = useUpdateCategory();
  const uploadFileMutation = useUploadFile();
  const { themes } = useGetThemes();
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    themeId: category.themeId.toString(),
    title: category.title,
    description: category.description,
    image: category.image,
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFormData({ ...formData, image: file.name });
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFormData({ ...formData, image: category.image }); // Reset to original image
  };

  const handleUpdate = async () => {
    if (!formData.themeId || !formData.title || !formData.description) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      let imageUrl = category.image; // Use existing image by default

      // Upload new image if one was selected
      if (selectedFile) {
        toast.info("Upload de l'image en cours...");
        const uploadResult = await uploadFileMutation.mutateAsync(selectedFile);
        imageUrl = uploadResult.url;
      }

      const payload: UpdateCategoryRequest = {
        themeId: parseInt(formData.themeId, 10),
        title: formData.title,
        description: formData.description,
        image: imageUrl,
      };

      mutation.mutate(
        { id: category.id, payload },
        {
          onSuccess: (data) => {
            setOpen(false);
            setSelectedFile(null);
            refetch();
            toast.success(
              `La catégorie ${data.title} a été modifiée avec succès!`,
              {
                duration: 3000,
              },
            );
          },
          onError: () => {
            toast.error(`Erreur lors de la modification de la catégorie`, {
              duration: 3000,
            });
          },
        },
      );
    } catch (error) {
      toast.error("Erreur lors de l'upload de l'image");
    }
  };

  const isLoading = uploadFileMutation.isPending || mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Edit size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-center">
            Modifier la catégorie
          </DialogTitle>
          <DialogDescription className="text-center">
            Modifier les informations de la catégorie
          </DialogDescription>
        </DialogHeader>

        <div className="w-full flex flex-col space-y-4">
          <div>
            <Select
              value={formData.themeId}
              onValueChange={(value) =>
                setFormData({ ...formData, themeId: value })
              }
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un thème" />
              </SelectTrigger>
              <SelectContent>
                {themes?.map((theme) => (
                  <SelectItem key={theme.id} value={theme.id.toString()}>
                    {theme.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Input
              type="text"
              placeholder="Titre de la catégorie"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              disabled={isLoading}
            />
          </div>

          <div>
            <Input
              type="text"
              placeholder="Description de la catégorie"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Image</label>
            {!selectedFile ? (
              <div>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <Upload className="mx-auto h-8 w-8 text-gray-400" />
                  <div className="mt-2">
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <span className="text-blue-600 hover:text-blue-500">
                        Cliquez pour changer l'image
                      </span>
                    </label>
                    <input
                      id="file-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileSelect}
                      disabled={isLoading}
                    />
                  </div>
                </div>
                {category.image && (
                  <p className="text-xs text-gray-500 mt-2">
                    Image actuelle: {category.image}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 p-2 rounded">
                    <Upload className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveFile}
                  disabled={isLoading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <DialogClose asChild>
            <Button variant="ghost" disabled={isLoading}>
              Annuler
            </Button>
          </DialogClose>
          <Button variant="default" onClick={handleUpdate} disabled={isLoading}>
            {isLoading ? 'Modification...' : 'Modifier'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
